import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import './CleanerApp.css';
import { Lang, Key, LANGS, DICT, DOW, CAL_DOW, ISSUE_PRESETS, detectLang, saveLang, monthTitle, presetText, serverMsg } from './cleanerI18n';

// 言語（日本語・中文・English）。t('key', { n: 1 }) で {n} を差し込む
interface I18n { lang: Lang; setLang: (l: Lang) => void; t: (k: Key, v?: Record<string, string | number>) => string; }
const I18nCtx = createContext<I18n>({ lang: 'ja', setLang: () => {}, t: (k) => DICT.ja[k] });
const useI18n = () => useContext(I18nCtx);
function makeT(lang: Lang) {
  return (k: Key, v?: Record<string, string | number>) => {
    let s = DICT[lang][k] ?? DICT.ja[k] ?? k;
    if (v) for (const [a, b] of Object.entries(v)) s = s.split(`{${a}}`).join(String(b));
    return s;
  };
}

// 清掃者向け画面  /cleaner   設計: §7（手順6・UI改善 2026-10-28）
//   表示: 「本日から1週間」（日付ごとの一覧）／「カレンダー」（月表示）／「未完了」
//   スマホは1列、PCは「一覧＋詳細」の2列。時刻はサーバーが記録する。
interface Mate { name: string; is_lead: number; is_me: number; }
interface Task {
  id: number; property_name: string | null; property_id: string;
  checkout_time: string | null; checkin_time: string | null;
  cleaning_date: string; checkin_date: string | null; status: string;
  planned_start_at: string | null; planned_end_at: string | null;
  actual_start_at: string | null; actual_end_at: string | null;
  report_note: string | null; issue_reason: string | null; supply_cost: number | null;
  admin_note: string | null; assignees?: Mate[];
  can_edit?: number; edit_block?: string | null;   // 完了報告を当日中に修正・取り消しできるか
  review_status?: 'approved' | 'rejected' | null;
  comments?: { kind: string; body: string | null; author_type: string; author_name: string | null; created_at: string }[];
  photos?: Photo[];
  checklist?: { id: number; label: string }[];              // 清掃チェックリスト（2026-10-31）
  checklist_done?: { item_id: number | null; label: string; checked_at: string }[];
}
// 報告写真（2026-10-29）
interface Photo { id: number; category: string; caption: string | null; created_at: string; }
const PHOTO_CATS = ['before', 'after', 'lost', 'damage', 'other'] as const;
type PhotoCat = typeof PHOTO_CATS[number];
interface Draft { key: string; file: File; url: string; category: PhotoCat; caption: string; }
const MAX_PHOTOS = 30;
const photoSrc = (taskId: number, pid: number) => `/api/cleaner/tasks/${taskId}/photos/${pid}`;

// 送る前に縮小（長辺1600px・JPEG）。通信量を減らし、iPhone の HEIC も JPEG にする
async function shrinkImage(file: File): Promise<Blob> {
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((ok, ng) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ng; i.src = url; });
    const s = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const cv = document.createElement('canvas');
    cv.width = Math.round(img.naturalWidth * s); cv.height = Math.round(img.naturalHeight * s);
    cv.getContext('2d')!.drawImage(img, 0, 0, cv.width, cv.height);
    URL.revokeObjectURL(url);
    const out = await new Promise<Blob | null>((ok) => cv.toBlob(ok, 'image/jpeg', 0.85));
    return out || file;
  } catch { return file; }
}
async function uploadPhoto(taskId: number, d: Draft): Promise<void> {
  const fd = new FormData();
  fd.append('file', await shrinkImage(d.file), 'photo.jpg');
  fd.append('category', d.category);
  fd.append('caption', d.caption.trim());
  const res = await fetch(`/api/cleaner/tasks/${taskId}/photos`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) throw new Error(String(res.status));
}
interface Notice { id: number; task_id: number | null; kind: string; title: string; body: string | null; read_at: string | null; created_at: string; }
type View = 'week' | 'calendar' | 'open';
const VIEWS: View[] = ['week', 'calendar', 'open'];
const STATUS: Record<string, { cls: string; icon: string }> = {
  pending: { cls: 'pending', icon: '🧹' },
  in_progress: { cls: 'progress', icon: '🔄' },
  completed: { cls: 'done', icon: '✅' },
  needs_review: { cls: 'review', icon: '⚠️' },
};
const stLabel = (t: I18n['t'], s: string) => (STATUS[s] ? t(`st.${s}` as Key) : s);

const jstToday = () => new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10);
function addDays(ymd: string, n: number): string {
  const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}
const dowOf = (ymd: string) => new Date(ymd + 'T00:00:00Z').getUTCDay();
const md = (ymd: string) => `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;
// 画面の言語は描画時に決まるので、モジュール変数で現在の言語を持つ（I18nProvider が更新）
let CUR: Lang = 'ja';
const dateLabel = (ymd: string) => (CUR === 'en' ? `${DOW.en[dowOf(ymd)]} ${md(ymd)}` : `${md(ymd)}(${DOW[CUR][dowOf(ymd)]})`);
const isoDay = (iso: string) => new Date(new Date(iso).getTime() + 9 * 3600e3).toISOString().slice(0, 10);
function hm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : new Date(d.getTime() + 9 * 3600e3).toISOString().slice(11, 16);
}
function planned(t: Task): string {
  const a = t.planned_start_at?.slice(11, 16) || t.checkout_time || '';
  const b = t.planned_end_at?.slice(11, 16) || t.checkin_time || '';
  return a || b ? `${a || '—'} 〜 ${b || '—'}` : makeT(CUR)('noTime');
}
function dayTitle(ymd: string, today: string): string {
  const t = makeT(CUR);
  if (ymd === today) return `${t('today')} ${dateLabel(ymd)}`;
  if (ymd === addDays(today, 1)) return `${t('tomorrow')} ${dateLabel(ymd)}`;
  if (ymd < today) return `${dateLabel(ymd)}${t('past')}`;
  return dateLabel(ymd);
}

async function call(path: string, method = 'GET', body?: unknown) {
  const res = await fetch(`/api/cleaner${path}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await res.json().catch(() => ({} as any));
  if (!res.ok) { const e: any = new Error(serverMsg(CUR, d?.error) || makeT(CUR)('genericErr', { s: res.status })); e.status = res.status; throw e; }
  return d;
}

export default function CleanerApp() {
  const [lang, setLangState] = useState<Lang>(() => { const l = detectLang(); CUR = l; saveLang(l); return l; });
  const i18n = useMemo<I18n>(() => {
    CUR = lang;
    return { lang, setLang: (l: Lang) => { CUR = l; saveLang(l); setLangState(l); }, t: makeT(lang) };
  }, [lang]);
  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang;
    document.title = i18n.t('appName');
  }, [lang, i18n]);
  return <I18nCtx.Provider value={i18n}><Root /></I18nCtx.Provider>;
}

function Root() {
  const { t } = useI18n();
  const [me, setMe] = useState<{ id: number; name: string } | null | undefined>(undefined);
  useEffect(() => {
    call('/me').then((d) => setMe(d.user || null)).catch(() => setMe(null));
  }, []);
  if (me === undefined) return <div className="cl-page"><p className="cl-center">{t('loading')}</p></div>;
  if (!me) return <Login onDone={(u) => setMe(u)} />;
  return <Home me={me} onLogout={() => setMe(null)} />;
}

// 言語の切り替え（ドロップダウン）。選んだ言語は端末に保存し、次回もその言語で開く
function LangSwitch() {
  const { lang, setLang, t } = useI18n();
  return (
    <label className="cl-lang">
      <span className="cl-lang-icon" aria-hidden>🌐</span>
      <select value={lang} onChange={(e) => setLang(e.target.value as Lang)} aria-label={t('language')}>
        {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------- ログイン
function Login({ onDone }: { onDone: (u: { id: number; name: string }) => void }) {
  const { t } = useI18n();
  const [loginId, setLoginId] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const payload: Record<string, string> = { login_id: loginId.trim() };
      payload['pass' + 'word'] = pw;
      const d = await call('/login', 'POST', payload);
      setPw(''); onDone(d.user);
    } catch (e: any) { setErr(e instanceof TypeError ? t('cannotConnect') : e.message); }
    finally { setBusy(false); }
  }
  return (
    <div className="cl-page cl-login-page">
      <form className="cl-login-card" onSubmit={submit}>
        <LangSwitch />
        <div className="cl-brand"><span className="cl-brand-icon">🧹</span><div><strong>Stay Dream</strong><span>{t('appName')}</span></div></div>
        <label className="cl-field">{t('loginId')}
          <input value={loginId} onChange={(e) => setLoginId(e.target.value)} name="username" autoComplete="username"
            autoCapitalize="none" autoCorrect="off" spellCheck={false} autoFocus />
        </label>
        <label className="cl-field">{t('pw')}
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} name="password" autoComplete="current-password" />
        </label>
        {err && <p className="cl-err" role="alert">⚠ {err}</p>}
        <button className="cl-btn cl-btn-blue" disabled={busy || !loginId || !pw}>{busy ? t('checking') : t('login')}</button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- ホーム
function Home({ me, onLogout }: { me: { id: number; name: string }; onLogout: () => void }) {
  const { t, lang } = useI18n();
  const today = jstToday();
  const [view, setView] = useState<View>('week');
  const [month, setMonth] = useState(today.slice(0, 7));
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [selId, setSelId] = useState<number | null>(null);
  const [calDay, setCalDay] = useState<string>(today);
  // お知らせ（管理者からのコメント・承認・却下）
  const [notices, setNotices] = useState<Notice[]>([]);
  const [unread, setUnread] = useState(0);
  const [showNotices, setShowNotices] = useState(false);
  const loadNotices = useCallback(async () => {
    try { const d = await call('/notifications'); setNotices(d.notifications || []); setUnread(d.unread || 0); } catch { /* 無視 */ }
  }, []);
  useEffect(() => {
    void loadNotices();
    const iv = window.setInterval(() => { if (document.visibilityState === 'visible') void loadNotices(); }, 60_000);
    return () => window.clearInterval(iv);
  }, [loadNotices]);
  async function openNotice(n: Notice) {
    setShowNotices(false);
    if (!n.read_at) await call('/notifications/read', 'POST', { ids: [n.id] }).catch(() => {});
    if (n.task_id) setSelId(n.task_id);
    void loadNotices();
  }
  // 上部バナー：最新の未読のお知らせ1件（×で既読にして閉じる）
  const latest = notices.find((n) => !n.read_at) || null;
  async function dismiss(n: Notice) {
    setNotices((list) => list.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
    setUnread((u) => Math.max(0, u - 1));
    await call('/notifications/read', 'POST', { ids: [n.id] }).catch(() => {});
    void loadNotices();
  }

  async function readAll() {
    await call('/notifications/read', 'POST', {}).catch(() => {});
    void loadNotices();
  }

  // 言語を切り替えたら一覧を読み直す（サーバーのエラー文などを新しい言語で出すため）
  const load = useCallback(async () => {
    void lang;
    setLoading(true); setMsg('');
    const q = view === 'calendar' ? `range=month&month=${month}` : `range=${view}`;
    try { const d = await call(`/tasks?${q}`); setTasks(d.tasks || []); }
    catch (e: any) { if (e.status === 401) onLogout(); else setMsg(e.message); }
    finally { setLoading(false); }
  }, [view, month, onLogout, lang]);
  useEffect(() => { void load(); }, [load]);

  async function logout() { await call('/logout', 'POST').catch(() => {}); onLogout(); }

  // 今週の集計（ヘッダーのサマリー）
  const summary = useMemo(() => {
    const todays = tasks.filter((t) => t.cleaning_date === today);
    return {
      today: todays.length,
      todayLeft: todays.filter((t) => t.status !== 'completed').length,
      review: tasks.filter((t) => t.status === 'needs_review').length,
    };
  }, [tasks, today]);

  const listForSide: Task[] = view === 'calendar' ? tasks.filter((t) => t.cleaning_date === calDay) : tasks;

  return (
    <div className="cl-page">
      <header className="cl-top">
        <div className="cl-top-inner">
          <div className="cl-brand cl-brand-text"><strong>{t('headerTitle')}</strong><span>{dateLabel(today)}</span></div>
          <div className="cl-top-right">
            <LangSwitch />
            <button className="cl-bell" onClick={() => setShowNotices((v) => !v)} aria-label={t('noticesAria', { n: unread })}>
              🔔{unread > 0 && <span className="cl-bell-badge">{unread > 99 ? '99+' : unread}</span>}
            </button>
            <span className="cl-me">👤 {me.name}</span>
            <button className="cl-icon-btn" onClick={logout} aria-label={t('logout')} title={t('logout')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {showNotices && (
        <div className="cl-notice-overlay" onClick={() => setShowNotices(false)}>
          <div className="cl-notice-panel" onClick={(e) => e.stopPropagation()}>
            <div className="cl-notice-head">
              <strong>{t('notices')}</strong>
              {unread > 0 && <button className="cl-link" onClick={() => void readAll()}>{t('readAll')}</button>}
            </div>
            {notices.length === 0 ? <p className="cl-center">{t('noNotices')}</p> : (
              <ul className="cl-notice-list">
                {notices.map((n) => (
                  <li key={n.id}>
                    <button className={`cl-notice k-${n.kind} ${n.read_at ? '' : 'unread'}`} onClick={() => void openNotice(n)}>
                      <span className="cl-notice-title">{n.kind === 'reject' ? '✖ ' : n.kind === 'approve' ? '✔ ' : '💬 '}{noticeTitle(t, n)}</span>
                      {n.body && <span className="cl-notice-body">{n.body}</span>}
                      <span className="cl-notice-time">{dateLabel(isoDay(n.created_at))} {hm(n.created_at)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <main className="cl-main">
        {latest && (
          <div className={`cl-banner k-${latest.kind}`} role="status" aria-live="polite">
            <span className="cl-banner-icon" aria-hidden>{latest.kind === 'reject' ? '✖' : latest.kind === 'approve' ? '✔' : '💬'}</span>
            <button className="cl-banner-body" onClick={() => void openNotice(latest)}>
              <span className="cl-banner-title">
                {noticeTitle(t, latest)}
                <span className="cl-banner-time">{dateLabel(isoDay(latest.created_at))} {hm(latest.created_at)}</span>
              </span>
              {latest.body && <span className="cl-banner-text">{latest.body}</span>}
              <span className="cl-banner-more">
                {latest.task_id ? t('viewDetail') : ''}
                {unread > 1 && <span className="cl-banner-others" onClick={(e) => { e.stopPropagation(); setShowNotices(true); }}>{t('otherUnread', { n: unread - 1 })}</span>}
              </span>
            </button>
            <button className="cl-banner-close" onClick={() => void dismiss(latest)} aria-label={t('closeNotice')}>×</button>
          </div>
        )}
        {view === 'week' && !loading && (
          <div className="cl-summary">
            <div className="cl-sum"><span>{t('sumToday')}</span><strong>{summary.today}</strong><small>{t('unit')}</small></div>
            <div className="cl-sum"><span>{t('sumLeft')}</span><strong className={summary.todayLeft ? 'warn' : ''}>{summary.todayLeft}</strong><small>{t('unit')}</small></div>
            <div className="cl-sum"><span>{t('sumWeek')}</span><strong>{tasks.length}</strong><small>{t('unit')}</small></div>
          </div>
        )}

        <nav className="cl-seg" role="tablist">
          {VIEWS.map((v) => (
            <button key={v} role="tab" aria-selected={view === v} className={view === v ? 'active' : ''}
              onClick={() => { setView(v); setSelId(null); }}>{t(`view.${v}` as Key)}</button>
          ))}
        </nav>
        {msg && <p className="cl-err">⚠ {msg}</p>}

        <div className={`cl-layout ${selId != null ? 'has-detail' : ''}`}>
          <section className="cl-col-list">
            {loading ? <p className="cl-center">{t('loading')}</p> : view === 'calendar' ? (
              <>
                <Calendar month={month} today={today} tasks={tasks} selected={calDay}
                  onMonth={(m) => { setMonth(m); setSelId(null); }}
                  onPick={(d) => { setCalDay(d); setSelId(null); }} />
                <h2 className="cl-day-title">{dayTitle(calDay, today)}</h2>
                <TaskCards tasks={listForSide} selId={selId} onOpen={setSelId} empty={t('noTaskDay')} />
              </>
            ) : view === 'week' ? (
              <WeekList today={today} tasks={tasks} selId={selId} onOpen={setSelId} />
            ) : (
              <TaskCards tasks={tasks} selId={selId} onOpen={setSelId} showDate empty={t('noOpen')} />
            )}
            <button className="cl-btn cl-btn-ghost cl-reload" onClick={() => void load()}>{t('refresh')}</button>
          </section>

          <section className="cl-col-detail">
            {selId != null ? (
              <Detail key={selId} id={selId} onClose={() => setSelId(null)} onChanged={() => { void load(); void loadNotices(); }} onLogout={onLogout} />
            ) : (
              <div className="cl-detail-empty">
                <span>👈</span>
                <p>{t('pickHint')}</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

// 本日から1週間：日付ごとに区切って表示（担当がない日も表示）
function WeekList({ today, tasks, selId, onOpen }: { today: string; tasks: Task[]; selId: number | null; onOpen: (id: number) => void }) {
  const { t } = useI18n();
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));
  return (
    <div className="cl-week">
      {days.map((d) => {
        const list = tasks.filter((t) => t.cleaning_date === d);
        const dow = dowOf(d);
        return (
          <div key={d} className={`cl-day ${d === today ? 'is-today' : ''}`}>
            <h2 className="cl-day-title">
              <span className={dow === 0 ? 'sun' : dow === 6 ? 'sat' : ''}>{dayTitle(d, today)}</span>
              <span className="cl-day-count">{list.length ? t('countN', { n: list.length }) : ''}</span>
            </h2>
            {list.length ? <TaskCards tasks={list} selId={selId} onOpen={onOpen} /> : <p className="cl-day-none">{t('dayNone')}</p>}
          </div>
        );
      })}
    </div>
  );
}

function TaskCards({ tasks, selId, onOpen, showDate, empty }: {
  tasks: Task[]; selId: number | null; onOpen: (id: number) => void; showDate?: boolean; empty?: string;
}) {
  const { t: tr } = useI18n();
  if (!tasks.length) return <p className="cl-center cl-empty">{empty || tr('noTasks')}</p>;
  return (
    <ul className="cl-cards">
      {tasks.map((t) => {
        const st = STATUS[t.status] || { cls: '', icon: '' };
        const mates = (t.assignees || []).filter((a) => !a.is_me).map((a) => a.name);
        return (
          <li key={t.id}>
            <button className={`cl-card st-${st.cls} ${selId === t.id ? 'selected' : ''}`} onClick={() => onOpen(t.id)}>
              <span className="cl-card-time">{planned(t).split(' 〜 ')[0]}</span>
              <span className="cl-card-body">
                <span className="cl-card-top">
                  <span className="cl-prop">{t.property_name || t.property_id}</span>
                  <span className={`cl-badge st-${st.cls}`}>{st.icon} {stLabel(tr, t.status)}</span>
                </span>
                <span className="cl-card-sub">
                  {showDate && <>{dateLabel(t.cleaning_date)}　</>}🕘 {planned(t)}
                </span>
                {mates.length > 0 && <span className="cl-card-sub">👥 {tr('withMates', { names: mates.join(tr('sep')) })}</span>}
              </span>
              <span className="cl-chev" aria-hidden>›</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// 月カレンダー（月曜始まり）。日付をタップするとその日の担当を下に表示
function Calendar({ month, today, tasks, selected, onMonth, onPick }: {
  month: string; today: string; tasks: Task[]; selected: string;
  onMonth: (m: string) => void; onPick: (d: string) => void;
}) {
  const { t, lang } = useI18n();
  const first = `${month}-01`;
  const shift = (n: number) => {
    const d = new Date(first + 'T00:00:00Z'); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7);
  };
  const start = addDays(first, -((dowOf(first) + 6) % 7));
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const weeks = cells[35].slice(0, 7) === month ? 6 : 5;
  const byDay = new Map<string, Task[]>();
  for (const t of tasks) { const l = byDay.get(t.cleaning_date) || []; l.push(t); byDay.set(t.cleaning_date, l); }

  return (
    <div className="cl-cal">
      <div className="cl-cal-head">
        <button className="cl-cal-nav" onClick={() => onMonth(shift(-1))} aria-label={t('prevMonth')}>‹</button>
        <strong>{monthTitle(lang, month)}</strong>
        <button className="cl-cal-nav" onClick={() => onMonth(shift(1))} aria-label={t('nextMonth')}>›</button>
        {month !== today.slice(0, 7) && <button className="cl-link cl-cal-today" onClick={() => { onMonth(today.slice(0, 7)); onPick(today); }}>{t('todayBtn')}</button>}
      </div>
      <div className="cl-cal-grid">
        {CAL_DOW[lang].map((w, i) => <div key={w} className={`cl-cal-dow ${i === 5 ? 'sat' : i === 6 ? 'sun' : ''}`}>{w}</div>)}
        {cells.slice(0, weeks * 7).map((d) => {
          const list = byDay.get(d) || [];
          const out = d.slice(0, 7) !== month;
          const dow = dowOf(d);
          return (
            <button key={d} className={`cl-cal-cell ${out ? 'out' : ''} ${d === today ? 'today' : ''} ${d === selected ? 'sel' : ''}`}
              onClick={() => onPick(d)} aria-label={t('dayAria', { d: dateLabel(d), n: list.length })}>
              <span className={`cl-cal-num ${dow === 0 ? 'sun' : dow === 6 ? 'sat' : ''}`}>{Number(d.slice(8, 10))}</span>
              <span className="cl-cal-dots">
                {list.slice(0, 3).map((t) => <i key={t.id} className={`dot st-${(STATUS[t.status] || { cls: '' }).cls}`} />)}
                {list.length > 3 && <em>+{list.length - 3}</em>}
              </span>
              {list.length > 0 && <span className="cl-cal-names">{list.slice(0, 2).map((t) => <span key={t.id}>{t.property_name || t.property_id}</span>)}</span>}
            </button>
          );
        })}
      </div>
      <div className="cl-legend">
        {Object.entries(STATUS).map(([k, s]) => <span key={s.cls}><i className={`dot st-${s.cls}`} />{stLabel(t, k)}</span>)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- 詳細・報告
function Detail({ id, onClose, onChanged, onLogout }: { id: number; onClose: () => void; onChanged: () => void; onLogout: () => void }) {
  const { t: tr, lang } = useI18n();
  const [t, setT] = useState<Task | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'none' | 'complete' | 'edit' | 'issue' | 'undo' | 'photo'>('none');
  const [note, setNote] = useState('');
  const [cost, setCost] = useState('');
  const [reason, setReason] = useState('');
  const [confirmStart, setConfirmStart] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [progress, setProgress] = useState('');
  const clearDrafts = () => setDrafts((list) => { list.forEach((d) => URL.revokeObjectURL(d.url)); return []; });

  const load = useCallback(async () => {
    setErr('');
    try { const d = await call(`/tasks/${id}`); setT(d.task); }
    catch (e: any) { if (e.status === 401) onLogout(); else setErr(e.message); }
  }, [id, onLogout, lang]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [id]);

  async function act(path: string, body?: unknown) {
    setBusy(true); setErr('');
    try { await call(`/tasks/${id}/${path}`, 'POST', body || {}); setMode('none'); await load(); onChanged(); }
    catch (e: any) { if (e.status === 401) onLogout(); else setErr(e.message); }
    finally { setBusy(false); setConfirmStart(false); }
  }

  // 作業開始 → 続けて清掃前の写真を送る（写真は任意）
  async function startWithPhotos() {
    setBusy(true); setErr('');
    try { await call(`/tasks/${id}/start`, 'POST', {}); }
    catch (e: any) { setBusy(false); setConfirmStart(false); if (e.status === 401) onLogout(); else setErr(e.message); return; }
    let failed = 0;
    for (let i = 0; i < drafts.length; i++) {
      setProgress(tr('photoUploading', { i: i + 1, n: drafts.length }));
      try { await uploadPhoto(id, drafts[i]); } catch { failed++; }
    }
    clearDrafts(); setProgress(''); setBusy(false); setConfirmStart(false);
    await load(); onChanged();
    if (failed) setErr(tr('photoOnlyFail', { n: failed }));
  }

  // 完了報告・報告の修正 → 続けて写真を1枚ずつ送る（写真は任意）
  async function submitReport(cost: string, checkedIds: number[] = []) {
    setBusy(true); setErr('');
    try {
      await call(`/tasks/${id}/${mode === 'edit' ? 'report' : 'complete'}`, 'POST', { note, supply_cost: cost, checked_ids: checkedIds });
      let failed = 0;
      for (let i = 0; i < drafts.length; i++) {
        setProgress(tr('photoUploading', { i: i + 1, n: drafts.length }));
        try { await uploadPhoto(id, drafts[i]); } catch { failed++; }
      }
      clearDrafts(); setMode('none'); await load(); onChanged();
      if (failed) setErr(tr('photoFail', { n: failed }));
    } catch (e: any) { if (e.status === 401) onLogout(); else setErr(e.message); }
    finally { setBusy(false); setProgress(''); }
  }

  const back = <button className="cl-back" onClick={onClose}>{tr('back')}</button>;
  if (!t) return <div className="cl-detail">{back}{err ? <p className="cl-err">⚠ {err}</p> : <p className="cl-center">{tr('loading')}</p>}</div>;
  const st = STATUS[t.status] || { cls: '', icon: '' };
  const canStart = t.status === 'pending';
  const canComplete = ['pending', 'in_progress', 'needs_review'].includes(t.status);

  return (
    <div className="cl-detail">
      {back}
      <div className={`cl-detail-card st-${st.cls}`}>
        <div className="cl-detail-head">
          <div>
            <div className="cl-detail-date">{dateLabel(t.cleaning_date)}</div>
            <h2 className="cl-detail-title">{t.property_name || t.property_id}</h2>
          </div>
          <span className={`cl-badge big st-${st.cls}`}>{st.icon} {stLabel(tr, t.status)}</span>
        </div>
        <div className="cl-time-box">
          <div><span>{tr('planned')}</span><strong>{planned(t)}</strong></div>
          {(t.actual_start_at || t.actual_end_at) && <div><span>{tr('actual')}</span><strong>{hm(t.actual_start_at) || '—'} 〜 {hm(t.actual_end_at) || '—'}</strong></div>}
        </div>
        {t.review_status === 'rejected' && t.status !== 'completed' && (
          <div className="cl-reject-banner">
            <strong>{tr('rejectedTitle')}</strong>
            <span>{tr('rejectedText')}</span>
          </div>
        )}
        {t.review_status === 'approved' && <div className="cl-approve-banner">{tr('approved')}</div>}
        <dl className="cl-dl">
          {t.checkin_date && (<><dt>{tr('nextCheckin')}</dt><dd>{t.checkin_date === t.cleaning_date ? tr('sameDay') : dateLabel(t.checkin_date)}</dd></>)}
          {(t.assignees || []).length > 0 && (<><dt>{tr('assignees')}</dt><dd>{(t.assignees || []).map((a) => `${a.is_lead ? '★' : ''}${a.name}${a.is_me ? tr('you') : ''}`).join(tr('sep'))}</dd></>)}
          {t.admin_note && (<><dt>{tr('adminNote')}</dt><dd className="cl-pre cl-note-box">{t.admin_note}</dd></>)}
          {t.issue_reason && (<><dt>{tr('issueReason')}</dt><dd className="cl-pre cl-issue-box">{t.issue_reason}</dd></>)}
          {t.report_note && (<><dt>{tr('reportNote')}</dt><dd className="cl-pre">{t.report_note}</dd></>)}
          {t.supply_cost != null && (<><dt>{tr('supplyCost')}</dt><dd>{tr('yen', { n: Number(t.supply_cost).toLocaleString() })}</dd></>)}
        </dl>
        {t.status === 'completed' && (t.checklist_done || []).length > 0 && (
          <div className="cl-checklist done">
            <div className="cl-checklist-title">{tr('checklistDoneTitle')}</div>
            <ul>{(t.checklist_done || []).map((c, i) => <li key={i}>✅ {c.label}</li>)}</ul>
          </div>
        )}
        <PhotoGallery taskId={t.id} photos={t.photos || []}
          canDelete={['pending', 'in_progress', 'needs_review'].includes(t.status) || !!t.can_edit}
          onChanged={() => void load()} onError={setErr} />
        {(t.comments || []).length > 0 && (
          <div className="cl-comments">
            <div className="cl-comments-title">{tr('commentsTitle')}</div>
            {(t.comments || []).map((c, i) => (
              <div key={i} className={`cl-comment k-${c.kind}`}>
                <div className="cl-comment-head">
                  <span>{tr(c.kind === 'reject' ? 'kind.reject' : c.kind === 'approve' ? 'kind.approve' : 'kind.comment')}</span>
                  <span className="cl-comment-time">{dateLabel(isoDay(c.created_at))} {hm(c.created_at)}</span>
                </div>
                {c.body && <div className="cl-pre">{c.body}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
      {err && <p className="cl-err" role="alert">⚠ {err}</p>}

      {(
        <div className="cl-actions">
          {canStart && <button className="cl-btn cl-btn-blue" disabled={busy} onClick={() => { clearDrafts(); setConfirmStart(true); }}>{tr('start')}</button>}
          {canComplete && <button className="cl-btn cl-btn-green" disabled={busy} onClick={() => {
            // 取り消したあと再報告するときは、前のメモ・備品代を入れておく
            setNote(t.report_note || ''); setCost(t.supply_cost != null ? String(t.supply_cost) : ''); setMode('complete');
          }}>{tr('complete')}</button>}
          {t.status === 'completed' && (
            <div className="cl-done-box">
              <p className="cl-done-msg">{tr('doneMsg')}</p>
              {t.can_edit ? (
                <>
                  <p className="cl-edit-hint">{tr('editHint')}</p>
                  <div className="cl-edit-btns">
                    <button className="cl-btn cl-btn-ghost" disabled={busy} onClick={() => {
                      setNote(t.report_note || ''); setCost(t.supply_cost != null ? String(t.supply_cost) : ''); setMode('edit');
                    }}>{tr('editReport')}</button>
                    <button className="cl-btn cl-btn-ghost danger" disabled={busy} onClick={() => setMode('undo')}>{tr('undo')}</button>
                  </div>
                </>
              ) : t.edit_block ? <p className="cl-edit-hint muted">{serverMsg(lang, t.edit_block)}</p> : null}
            </div>
          )}
        </div>
      )}
      {(mode === 'complete' || mode === 'edit') && (
        <CompleteSheet task={t} editing={mode === 'edit'} busy={busy} err={err} note={note} cost={cost} setNote={setNote} setCost={setCost}
          drafts={drafts} setDrafts={setDrafts} progress={progress}
          onCancel={() => { setMode('none'); setErr(''); clearDrafts(); }}
          onOk={(v, ids) => void submitReport(v, ids)} />
      )}
      {mode === 'undo' && (
        <UndoSheet task={t} busy={busy} err={err} onCancel={() => { setMode('none'); setErr(''); }} onOk={() => void act('undo-complete')} />
      )}
      {mode === 'issue' && (
        <IssueSheet task={t} busy={busy} err={err} reason={reason} setReason={setReason}
          onCancel={() => { setMode('none'); setErr(''); }}
          onOk={() => void act('issue', { reason })} />
      )}

      {confirmStart && (
        <StartConfirm task={t} busy={busy} drafts={drafts} setDrafts={setDrafts} progress={progress}
          onCancel={() => { setConfirmStart(false); clearDrafts(); }} onOk={() => void startWithPhotos()} />
      )}
    </div>
  );
}

// 作業開始の確認（ブラウザ標準の confirm の代わり）
//   スマホ: 画面下から出るシート / PC: 画面中央のダイアログ
function StartConfirm({ task, busy, drafts, setDrafts, progress, onCancel, onOk }: {
  task: Task; busy: boolean; drafts: Draft[]; setDrafts: (d: Draft[]) => void; progress: string; onCancel: () => void; onOk: () => void;
}) {
  const { t } = useI18n();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = window.setInterval(() => setNow(new Date()), 1000 * 15);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.clearInterval(iv); window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [busy, onCancel]);
  const nowHm = new Date(now.getTime() + 9 * 3600e3).toISOString().slice(11, 16);
  const plannedStart = task.planned_start_at?.slice(11, 16) || task.checkout_time || '';
  const early = plannedStart && task.cleaning_date === jstToday() && nowHm < plannedStart;
  const notToday = task.cleaning_date !== jstToday();
  return (
    <div className="cl-sheet-overlay" onClick={() => !busy && onCancel()}>
      <div className="cl-sheet" role="dialog" aria-modal="true" aria-labelledby="cl-start-title" style={{ maxHeight: '92vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="cl-sheet-grip" aria-hidden />
        <div className="cl-sheet-icon">▶</div>
        <h3 id="cl-start-title" className="cl-sheet-title">{t('startTitle')}</h3>
        <div className="cl-sheet-info">
          <div className="cl-sheet-prop">{task.property_name || task.property_id}</div>
          <div className="cl-sheet-rows">
            <div><span>{t('cleaningDate')}</span><strong>{dateLabel(task.cleaning_date)}</strong></div>
            <div><span>{t('planned')}</span><strong>{planned(task)}</strong></div>
            <div className="hl"><span>{t('startTime')}</span><strong>{nowHm}</strong></div>
          </div>
        </div>
        {notToday && <p className="cl-sheet-warn">{t('notTodayWarn', { d: dateLabel(task.cleaning_date) })}</p>}
        {!notToday && early && <p className="cl-sheet-warn">{t('earlyWarn', { t: plannedStart })}</p>}
        <p className="cl-sheet-note">{t('startNote')}</p>
        <PhotoPicker drafts={drafts} setDrafts={setDrafts} existing={(task.photos || []).length} disabled={busy} defaultCat="before" />
        <div className="cl-sheet-actions">
          <button className="cl-btn cl-btn-blue" disabled={busy} onClick={onOk} autoFocus>{busy ? (progress || t('recording')) : t('startOk')}</button>
          <button className="cl-btn cl-btn-ghost" disabled={busy} onClick={onCancel}>{t('cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- シート共通
//   スマホ: 画面下から出るシート / PC: 中央のダイアログ。Esc・外側タップで閉じる（送信中は閉じない）
function Sheet({ title, icon, tone, busy, onCancel, children }: {
  title: string; icon: string; tone: 'blue' | 'green' | 'red'; busy: boolean; onCancel: () => void; children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [busy, onCancel]);
  return (
    <div className="cl-sheet-overlay" onClick={() => !busy && onCancel()}>
      <div className="cl-sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="cl-sheet-grip" aria-hidden />
        <div className={`cl-sheet-icon ${tone}`}>{icon}</div>
        <h3 className="cl-sheet-title">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function nowHmJst(): string {
  return new Date(Date.now() + 9 * 3600e3).toISOString().slice(11, 16);
}

// 完了報告
function CompleteSheet({ task, editing, busy, err, note, cost, setNote, setCost, drafts, setDrafts, progress, onCancel, onOk }: {
  task: Task; editing?: boolean; busy: boolean; err: string; note: string; cost: string;
  drafts: Draft[]; setDrafts: (d: Draft[]) => void; progress: string;
  setNote: (v: string) => void; setCost: (v: string) => void; onCancel: () => void; onOk: (cost: string, checkedIds: number[]) => void;
}) {
  const { t } = useI18n();
  const [localErr, setLocalErr] = useState('');
  // 清掃チェックリスト（完了報告のときだけ。修正では表示しない）
  const items = editing ? [] : (task.checklist || []);
  const [checked, setChecked] = useState<Set<number>>(() => new Set());
  const left = items.filter((it) => !checked.has(it.id)).length;
  const toggle = (id: number) => setChecked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const started = hm(task.actual_start_at);
  const now = nowHmJst();
  function submit(e: React.FormEvent) {
    e.preventDefault();
    const v = cost.replace(/[,，円\s]/g, '').replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0));
    if (v && !/^\d+$/.test(v)) { setLocalErr(t('costErr')); return; }
    if (left > 0) { setLocalErr(t('checklistNeed')); return; }
    if (v !== cost) setCost(v);
    setLocalErr('');
    onOk(v, [...checked]);
  }
  return (
    <Sheet title={editing ? t('editTitle') : t('completeTitle')} icon={editing ? '✏️' : '✅'} tone="green" busy={busy} onCancel={onCancel}>
      <form className="cl-sheet-form" onSubmit={submit}>
        <div className="cl-sheet-info">
          <div className="cl-sheet-prop">{task.property_name || task.property_id}</div>
          <div className="cl-sheet-rows">
            <div><span>{t('cleaningDate')}</span><strong>{dateLabel(task.cleaning_date)}</strong></div>
            <div><span>{t('startTime')}</span><strong>{started || `${now}${t('nowSuffix')}`}</strong></div>
            <div className="hl green"><span>{t('endTime')}</span><strong>{editing ? (hm(task.actual_end_at) || '—') : now}</strong></div>
          </div>
        </div>
        {editing
          ? <p className="cl-sheet-note left">{t('editNote')}</p>
          : !started && <p className="cl-sheet-warn">{t('noStartWarn')}</p>}
        {items.length > 0 && (
          <fieldset className="cl-checklist">
            <legend className="cl-checklist-title">
              {t('checklistTitle')}<span className="cl-opt req">{t('required')}</span>
              <span className={`cl-checklist-left ${left ? '' : 'ok'}`}>{left ? t('checklistLeft', { n: left }) : '✔'}</span>
            </legend>
            <p className="cl-checklist-hint">{t('checklistHint')}</p>
            {items.map((it) => (
              <label key={it.id} className={`cl-check-row ${checked.has(it.id) ? 'on' : ''}`}>
                <input type="checkbox" checked={checked.has(it.id)} disabled={busy} onChange={() => toggle(it.id)} />
                <span>{it.label}</span>
              </label>
            ))}
            {left > 1 && <button type="button" className="cl-link-btn" disabled={busy} onClick={() => setChecked(new Set(items.map((i) => i.id)))}>{t('checklistAll')}</button>}
          </fieldset>
        )}
        <label className="cl-field">{t('noteLabel')}<span className="cl-opt">{t('optional')}</span>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('notePh')} />
        </label>
        <label className="cl-field">{t('costLabel')}<span className="cl-opt">{t('optional')}</span>
          <span className="cl-yen">
            <input inputMode="numeric" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
            <span>{t('yenUnit')}</span>
          </span>
        </label>
        <PhotoPicker drafts={drafts} setDrafts={setDrafts} existing={(task.photos || []).length} disabled={busy} />
        {(localErr || err) && <p className="cl-err" role="alert">⚠ {localErr || err}</p>}
        <div className="cl-sheet-actions">
          <button type="submit" className="cl-btn cl-btn-green" disabled={busy || left > 0}>{busy ? (progress || t('sending')) : editing ? t('saveEdit') : t('submitComplete')}</button>
          <button type="button" className="cl-btn cl-btn-ghost" disabled={busy} onClick={onCancel}>{t('cancel')}</button>
        </div>
      </form>
    </Sheet>
  );
}

// 報告写真の選択（完了報告・報告の修正シートの下）。写真ごとに種類と説明をつける
function PhotoPicker({ drafts, setDrafts, existing, disabled, defaultCat = 'after', hideLabel }: {
  drafts: Draft[]; setDrafts: (d: Draft[]) => void; existing: number; disabled: boolean; defaultCat?: PhotoCat; hideLabel?: boolean;
}) {
  const { t } = useI18n();
  const room = MAX_PHOTOS - existing - drafts.length;
  function add(files: FileList | null) {
    if (!files) return;
    const list = Array.from(files).filter((f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name)).slice(0, Math.max(0, room))
      .map((f): Draft => ({ key: Math.random().toString(36).slice(2), file: f, url: URL.createObjectURL(f), category: defaultCat, caption: '' }));
    setDrafts([...drafts, ...list]);
  }
  const upd = (k: string, v: Partial<Draft>) => setDrafts(drafts.map((d) => (d.key === k ? { ...d, ...v } : d)));
  const remove = (d: Draft) => { URL.revokeObjectURL(d.url); setDrafts(drafts.filter((x) => x.key !== d.key)); };
  return (
    <div className="cl-field cl-photo-field">
      {!hideLabel && <>
        <span>{t('photosLabel')}<span className="cl-opt">{t('optional')}</span></span>
        <span className="cl-photo-hint">{t('photosHint')}</span>
      </>}
      {drafts.map((d) => (
        <div className="cl-photo-item" key={d.key}>
          <img src={d.url} alt="" />
          <div className="cl-photo-fields">
            <select value={d.category} disabled={disabled} onChange={(e) => upd(d.key, { category: e.target.value as PhotoCat })}>
              {PHOTO_CATS.map((c) => <option key={c} value={c}>{t(`pc.${c}` as Key)}</option>)}
            </select>
            <textarea rows={2} value={d.caption} maxLength={500} disabled={disabled} placeholder={t('captionPh')}
              onChange={(e) => upd(d.key, { caption: e.target.value })} />
          </div>
          <button type="button" className="cl-photo-x" disabled={disabled} onClick={() => remove(d)} aria-label={t('photoRemove')}>×</button>
        </div>
      ))}
      {room > 0 ? (
        <label className={`cl-btn cl-btn-ghost cl-photo-add ${disabled ? 'disabled' : ''}`}>
          {t('photoAdd')}
          <input type="file" accept="image/*" multiple hidden disabled={disabled} onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
        </label>
      ) : <span className="cl-photo-hint">{t('photoLimit', { n: MAX_PHOTOS })}</span>}
    </div>
  );
}

// 報告済みの写真（詳細画面）。タップで拡大
function PhotoGallery({ taskId, photos, canDelete, onChanged, onError }: {
  taskId: number; photos: Photo[]; canDelete: boolean; onChanged: () => void; onError: (m: string) => void;
}) {
  const { t } = useI18n();
  const [view, setView] = useState<Photo | null>(null);
  if (!photos.length) return null;
  async function del(p: Photo) {
    if (!window.confirm(t('photoDelConfirm'))) return;
    try { await call(`/tasks/${taskId}/photos/${p.id}`, 'DELETE'); onChanged(); } catch (e: any) { onError(e.message); }
  }
  return (
    <div className="cl-photos">
      <div className="cl-comments-title">{t('photosTitle', { n: photos.length })}</div>
      <div className="cl-photo-grid">
        {photos.map((p) => (
          <figure key={p.id} className="cl-photo-card">
            <button type="button" className="cl-photo-thumb" onClick={() => setView(p)}><img src={photoSrc(taskId, p.id)} loading="lazy" alt={p.caption || ''} /></button>
            <figcaption>
              <span className={`cl-pc pc-${p.category}`}>{t(`pc.${p.category}` as Key)}</span>
              {p.caption && <span className="cl-pre">{p.caption}</span>}
            </figcaption>
            {canDelete && <button type="button" className="cl-photo-x" onClick={() => void del(p)} aria-label={t('photoDelete')}>×</button>}
          </figure>
        ))}
      </div>
      {view && (
        <div className="cl-sheet-overlay cl-lightbox" onClick={() => setView(null)}>
          <img src={photoSrc(taskId, view.id)} alt={view.caption || ''} />
          <div className="cl-lightbox-cap"><span className={`cl-pc pc-${view.category}`}>{t(`pc.${view.category}` as Key)}</span> {view.caption}</div>
        </div>
      )}
    </div>
  );
}

// 問題を報告（要確認）
function IssueSheet({ task, busy, err, reason, setReason, onCancel, onOk }: {
  task: Task; busy: boolean; err: string; reason: string; setReason: (v: string) => void; onCancel: () => void; onOk: () => void;
}) {
  const { t, lang } = useI18n();
  function addPreset(p: string) {
    if (reason.includes(p)) return;
    setReason(reason.trim() ? `${reason.trim()}\n${p}` : p);
  }
  return (
    <Sheet title={t('issueTitle')} icon="⚠️" tone="red" busy={busy} onCancel={onCancel}>
      <form className="cl-sheet-form" onSubmit={(e) => { e.preventDefault(); if (reason.trim()) onOk(); }}>
        <div className="cl-sheet-info">
          <div className="cl-sheet-prop">{task.property_name || task.property_id}</div>
          <div className="cl-sheet-rows">
            <div><span>{t('cleaningDate')}</span><strong>{dateLabel(task.cleaning_date)}</strong></div>
          </div>
        </div>
        <p className="cl-sheet-note left">{t('issueNote')}</p>
        <div className="cl-chips" aria-label={t('presetsAria')}>
          {ISSUE_PRESETS.map((p) => {
            const txt = presetText(lang, p);
            return <button key={p.ja} type="button" className={`cl-chip ${reason.includes(p.ja) ? 'on' : ''}`} onClick={() => addPreset(txt)}>{p[lang]}</button>;
          })}
        </div>
        <label className="cl-field">{t('issueLabel')}<span className="cl-req">{t('required')}</span>
          <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('issuePh')} />
        </label>
        {err && <p className="cl-err" role="alert">⚠ {err}</p>}
        <div className="cl-sheet-actions">
          <button type="submit" className="cl-btn cl-btn-warn solid" disabled={busy || !reason.trim()}>{busy ? t('sending') : t('submitIssue')}</button>
          <button type="button" className="cl-btn cl-btn-ghost" disabled={busy} onClick={onCancel}>{t('cancel')}</button>
        </div>
      </form>
    </Sheet>
  );
}

// 完了の取り消し
function UndoSheet({ task, busy, err, onCancel, onOk }: { task: Task; busy: boolean; err: string; onCancel: () => void; onOk: () => void }) {
  const { t } = useI18n();
  return (
    <Sheet title={t('undoTitle')} icon="↩︎" tone="red" busy={busy} onCancel={onCancel}>
      <div className="cl-sheet-info">
        <div className="cl-sheet-prop">{task.property_name || task.property_id}</div>
        <div className="cl-sheet-rows">
          <div><span>{t('cleaningDate')}</span><strong>{dateLabel(task.cleaning_date)}</strong></div>
          <div><span>{t('recordedTime')}</span><strong>{hm(task.actual_start_at) || '—'} 〜 {hm(task.actual_end_at) || '—'}</strong></div>
        </div>
      </div>
      <ul className="cl-undo-list">
        <li>{t('undo1')}</li>
        <li>{t('undo2')}</li>
        <li>{t('undo3')}</li>
      </ul>
      {err && <p className="cl-err" role="alert">⚠ {err}</p>}
      <div className="cl-sheet-actions">
        <button className="cl-btn cl-btn-warn solid" disabled={busy} onClick={onOk}>{busy ? t('processing') : t('undoOk')}</button>
        <button className="cl-btn cl-btn-ghost" disabled={busy} onClick={onCancel}>{t('keep')}</button>
      </div>
    </Sheet>
  );
}

// お知らせの見出し（サーバーは日本語で保存しているので、種類から各言語の見出しにする）
function noticeTitle(t: I18n['t'], n: { kind: string; title: string }): string {
  if (n.kind === 'reject') return t('notice.reject');
  if (n.kind === 'approve') return t('notice.approve');
  if (n.kind === 'comment') return t('notice.comment');
  return n.title;
}

